import { useCallback, useEffect, useState } from 'react';
import { Receipt, Search } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { PaginaRecargas, StatusRecarga } from '../../api/tipos';
import {
  Abas,
  Aviso,
  BarraFerramentas,
  Campo,
  Cartao,
  Celula,
  Crescer,
  Esqueleto,
  Etiqueta,
  Indicador,
  Indicadores,
  Paginacao,
  Tabela,
  Td,
  Th,
  Vazio,
  type TomEtiqueta,
} from '../../componentes/ui';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import { dataHora, plural, reais } from '../../util/formato';
import css from './PagamentosAdmin.module.css';

const SITUACOES = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'PENDENTE', rotulo: 'Aguardando' },
  { valor: 'PAGA', rotulo: 'Pagas' },
  { valor: 'EXPIRADA', rotulo: 'Expiradas' },
  { valor: 'CANCELADA', rotulo: 'Canceladas' },
  { valor: 'ESTORNADA', rotulo: 'Estornadas' },
] as const;

type Situacao = (typeof SITUACOES)[number]['valor'];

const TOM: Record<StatusRecarga, TomEtiqueta> = {
  PAGA: 'ok',
  PENDENTE: 'alerta',
  EXPIRADA: 'neutro',
  CANCELADA: 'erro',
  ESTORNADA: 'erro',
};

/** O status vem em caixa alta da API; na tela, em sentença. */
const ROTULO: Record<StatusRecarga, string> = {
  PAGA: 'Paga',
  PENDENTE: 'Aguardando',
  EXPIRADA: 'Expirada',
  CANCELADA: 'Cancelada',
  ESTORNADA: 'Estornada (devolvida)',
};

const ATRASO_BUSCA_MS = 350;

/**
 * Solicitações de pagamento — a cobrança vista de DENTRO do sistema.
 *
 * A tela Financeiro mostra o mesmo dinheiro do lado do provedor. Ter as duas é
 * o que permite responder "o cliente pagou e o crédito não entrou": se a
 * recarga está PENDENTE aqui e recebida lá, o webhook não chegou.
 */
export function PagamentosAdmin() {
  const [situacao, setSituacao] = useState<Situacao>('todas');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<PaginaRecargas | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const q = new URLSearchParams({ pagina: String(pagina), porPagina: '25' });
    if (situacao !== 'todas') q.set('status', situacao);
    if (busca.trim()) q.set('busca', busca.trim());

    try {
      setErro(null);
      setDados(await api<PaginaRecargas>(`/admin/recargas?${q.toString()}`));
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao listar os pagamentos.',
      );
    }
  }, [situacao, busca, pagina]);

  useEffect(() => {
    const t = window.setTimeout(() => void carregar(), ATRASO_BUSCA_MS);
    return () => clearTimeout(t);
  }, [carregar]);

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Pagamentos"
        descricao="Toda recarga pedida no sistema, paga ou não. Os totais somam tudo, não só a página."
      />

      {erro && <Aviso>{erro}</Aviso>}

      {dados && dados.resumo.length > 0 && (
        <Indicadores>
          {dados.resumo.map((r) => (
            <Indicador
              key={r.status}
              rotulo={ROTULO[r.status]}
              valor={reais(r.valorCentavos)}
              detalhe={plural(r.quantidade, 'solicitação', 'solicitações')}
              tom={
                r.status === 'PAGA'
                  ? 'ok'
                  : r.status === 'PENDENTE'
                    ? 'alerta'
                    : undefined
              }
            />
          ))}
        </Indicadores>
      )}

      <Cartao padding={0}>
        <BarraFerramentas>
          <Crescer>
            <Campo
              rotulo="Buscar solicitações"
              rotuloOculto
              type="search"
              icone={<Search />}
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setPagina(1);
              }}
              placeholder="Buscar por cliente, e-mail ou id da cobrança"
            />
          </Crescer>
          <Abas
            rotulo="Situação"
            opcoes={SITUACOES}
            atual={situacao}
            aoMudar={(v) => {
              setSituacao(v);
              setPagina(1);
            }}
          />
        </BarraFerramentas>

        {!dados ? (
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        ) : dados.itens.length === 0 ? (
          <Vazio
            icone={<Receipt />}
            titulo="Nenhuma solicitação"
            descricao="Nada bate com a busca ou o filtro escolhido."
          />
        ) : (
          <Tabela rotulo="Solicitações de pagamento">
            <thead>
              <tr>
                <Th>Cliente</Th>
                <Th alinhar="direita">Valor</Th>
                <Th>Situação</Th>
                <Th>Cobrança no Asaas</Th>
                <Th>Pedida em</Th>
                <Th>Paga em</Th>
              </tr>
            </thead>
            <tbody>
              {dados.itens.map((r) => (
                <tr key={r.id}>
                  <Td>
                    <Celula principal={r.user.nome} secundaria={r.user.email} />
                  </Td>
                  <Td alinhar="direita">
                    <span className={css.valor}>{reais(r.valorCentavos)}</span>
                  </Td>
                  <Td>
                    <Etiqueta tom={TOM[r.status]}>{ROTULO[r.status]}</Etiqueta>
                  </Td>
                  <Td>
                    <span className={css.codigo}>
                      {r.asaasPaymentId ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <span className={css.data}>{dataHora(r.createdAt)}</span>
                  </Td>
                  <Td>
                    <span className={r.pagaEm ? css.paga : css.data}>
                      {r.pagaEm ? dataHora(r.pagaEm) : '—'}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}

        {dados && dados.itens.length > 0 && (
          <Paginacao
            total={dados.total}
            pagina={dados.pagina}
            porPagina={dados.porPagina}
            aoMudar={setPagina}
            rotulo={dados.total === 1 ? 'solicitação' : 'solicitações'}
          />
        )}
      </Cartao>
    </Pagina>
  );
}
